import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { DollarSign, TrendingUp, FileText, RefreshCw, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

/* ── Data ── */
const REVENUE_BY_VESSEL = [
  { name: "Crewliner 01", revenue: 462500, client: "ExxonMobil" },
  { name: "Combi 01", revenue: 388000, client: "Azule Energy" },
  { name: "Crewliner 02", revenue: 0, client: "Pending" },
];

const REVENUE_BY_CLIENT = [
  { name: "ExxonMobil", value: 462500, color: "#00283C" },
  { name: "Azule Energy", value: 388000, color: "#288CC8" },
];

const MONTHLY_REVENUE = [
  { month: "May", actual: 420000, target: 500000 },
  { month: "Jun", actual: 485000, target: 500000 },
  { month: "Jul", actual: 510000, target: 550000 },
  { month: "Aug", actual: 530000, target: 550000 },
  { month: "Sep", actual: 620000, target: 600000 },
  { month: "Oct", actual: 680000, target: 650000 },
  { month: "Nov", actual: 710000, target: 700000 },
  { month: "Dec", actual: 750000, target: 750000 },
  { month: "Jan", actual: 780000, target: 800000 },
  { month: "Feb", actual: 800000, target: 800000 },
  { month: "Mar", actual: 820000, target: 850000 },
  { month: "Apr", actual: 851250, target: 850000 },
];

interface Invoice {
  number: string; vessel: string; client: string; amount: number; issueDate: string; dueDate: string; status: "paid" | "current" | "draft" | "overdue_30" | "overdue_60";
}

const INVOICES: Invoice[] = [
  { number: "VOFF-INV-2026-001", vessel: "Crewliner 01", client: "ExxonMobil", amount: 462500, issueDate: "2026-03-01", dueDate: "2026-03-31", status: "paid" },
  { number: "VOFF-INV-2026-002", vessel: "Combi 01", client: "Azule Energy", amount: 388000, issueDate: "2026-04-01", dueDate: "2026-04-30", status: "current" },
  { number: "VOFF-INV-2026-003", vessel: "Crewliner 01", client: "ExxonMobil", amount: 218750, issueDate: "2026-04-15", dueDate: "—", status: "draft" },
];

interface VesselPL {
  vessel: string; client: string; cp: string; hireIncome: number;
  costs: { label: string; amount: number }[];
}

const VESSEL_PL: VesselPL[] = [
  { vessel: "VOFF CREWLINER 01", client: "ExxonMobil Guyana", cp: "XOM-GUY", hireIncome: 462500, costs: [{ label: "Crew wages", amount: 85000 }, { label: "Fuel", amount: 24500 }, { label: "Port/misc", amount: 8200 }, { label: "Management", amount: 15000 }] },
  { vessel: "VOFF COMBI 01", client: "Azule Energy", cp: "AZE-ANG", hireIncome: 388000, costs: [{ label: "Crew wages", amount: 78000 }, { label: "Fuel", amount: 21800 }, { label: "Port/misc", amount: 9500 }, { label: "Management", amount: 15000 }] },
];

const CASHFLOW = [
  { week: "W16", inflows: 218750, outflows: 65000, net: 153750, cumulative: 2253750 },
  { week: "W17", inflows: 0, outflows: 62000, net: -62000, cumulative: 2191750 },
  { week: "W18", inflows: 388000, outflows: 68000, net: 320000, cumulative: 2511750 },
  { week: "W19", inflows: 0, outflows: 64000, net: -64000, cumulative: 2447750 },
  { week: "W20", inflows: 0, outflows: 62000, net: -62000, cumulative: 2385750 },
  { week: "W21", inflows: 462500, outflows: 70000, net: 392500, cumulative: 2778250 },
  { week: "W22", inflows: 0, outflows: 63000, net: -63000, cumulative: 2715250 },
  { week: "W23", inflows: 0, outflows: 65000, net: -65000, cumulative: 2650250 },
  { week: "W24", inflows: 388000, outflows: 68000, net: 320000, cumulative: 2970250 },
  { week: "W25", inflows: 0, outflows: 62000, net: -62000, cumulative: 2908250 },
  { week: "W26", inflows: 218750, outflows: 64000, net: 154750, cumulative: 3063000 },
  { week: "W27", inflows: 0, outflows: 63000, net: -63000, cumulative: 3000000 },
];

const invoiceStatusBadge = (s: Invoice["status"]) => {
  const m: Record<string, { label: string; cls: string }> = {
    paid: { label: "PAID ✓", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    current: { label: "CURRENT", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
    draft: { label: "DRAFT", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
    overdue_30: { label: "OVERDUE 30", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
    overdue_60: { label: "OVERDUE 60+", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  };
  return <Badge variant="outline" className={`text-[10px] font-bold ${m[s].cls}`}>{m[s].label}</Badge>;
};

const FinancialPulse = () => {
  const [period, setPeriod] = useState("mtd");

  return (
    <IntranetLayout>
      {/* Header */}
      <div className="bg-[#00283C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-white text-xl font-bold tracking-[4px] uppercase font-display">Financial Pulse</h1>
            <p className="text-white/40 text-sm font-body mt-1">Treasury & Commercial Performance — Confidential</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[160px] border-white/20 text-white bg-transparent font-body text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="mtd">Month to Date</SelectItem><SelectItem value="qtd">Quarter to Date</SelectItem><SelectItem value="ytd">Year to Date</SelectItem></SelectContent>
            </Select>
            <Button onClick={() => toast.success("Refreshed: Refresh", { description: "Latest data loaded." })} size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 font-body text-xs">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Revenue Billed MTD", value: "$851,250", change: "▲ +12% vs LM", color: "text-emerald-400" },
          { label: "Cash Position (Indicative)", value: "~$2.1M", change: "Healthy", color: "text-emerald-400" },
          { label: "Outstanding Invoices", value: "$0", change: "All current", color: "text-emerald-400" },
          { label: "Fleet Utilisation", value: "94.2%", change: "▲ vs 91% LM", color: "text-emerald-400" },
        ].map(kpi => (
          <div key={kpi.label} className="bg-card border border-border rounded-xl p-5 text-center cursor-pointer hover:border-[#288CC8]/30 transition-colors">
            <p className={`text-2xl font-bold font-display ${kpi.color}`}>{kpi.value}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-body mt-1">{kpi.label}</p>
            <p className="text-[10px] text-muted-foreground font-body mt-0.5">{kpi.change}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue by Vessel */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-4">Revenue by Vessel</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={REVENUE_BY_VESSEL} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip formatter={(v: number) => [`$${v.toLocaleString()}`, "Revenue"]} />
              <Bar dataKey="revenue" fill="#288CC8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Client */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-4">Revenue by Client</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={REVENUE_BY_CLIENT} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {REVENUE_BY_CLIENT.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-4">Revenue Trend (12-Month)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={MONTHLY_REVENUE}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
              <Line type="monotone" dataKey="actual" stroke="#288CC8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="target" stroke="#8CB43C" strokeWidth={1} strokeDasharray="5 5" dot={false} />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Invoice Tracker */}
      <div className="bg-card border border-border rounded-xl p-5 mb-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-4">Invoice Ageing</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">Invoice No</th>
                <th className="text-left px-4 py-3">Vessel</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Issue Date</th>
                <th className="text-left px-4 py-3">Due Date</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {INVOICES.map(inv => (
                <tr key={inv.number} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs text-[#288CC8]">{inv.number}</td>
                  <td className="px-4 py-3 text-foreground">{inv.vessel}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.client}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground">${inv.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{inv.issueDate}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{inv.dueDate}</td>
                  <td className="px-4 py-3">{invoiceStatusBadge(inv.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vessel P&L */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {VESSEL_PL.map(vpl => {
          const totalCosts = vpl.costs.reduce((a, c) => a + c.amount, 0);
          const margin = vpl.hireIncome - totalCosts;
          const marginPct = ((margin / vpl.hireIncome) * 100).toFixed(1);
          const marginColor = parseFloat(marginPct) > 65 ? "text-emerald-400" : parseFloat(marginPct) > 50 ? "text-amber-400" : "text-red-400";
          return (
            <div key={vpl.vessel} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="bg-[#00283C] p-4">
                <h3 className="text-white text-sm font-bold font-display">{vpl.vessel}</h3>
                <p className="text-white/40 text-[10px] font-body">Client: {vpl.client} | CP: {vpl.cp}</p>
              </div>
              <div className="p-4 space-y-2 text-sm font-body">
                <div className="flex justify-between"><span className="text-muted-foreground">Hire Income</span><span className="text-foreground font-medium">${vpl.hireIncome.toLocaleString()}</span></div>
                <div className="border-t border-border pt-2 space-y-1">
                  {vpl.costs.map(c => (
                    <div key={c.label} className="flex justify-between"><span className="text-muted-foreground">{c.label}</span><span className="text-red-400">(${c.amount.toLocaleString()})</span></div>
                  ))}
                </div>
                <div className="border-t border-border pt-2 flex justify-between font-bold">
                  <span>Gross Margin</span>
                  <span className={marginColor}>${margin.toLocaleString()} ({marginPct}%)</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cash Flow */}
      <div className="bg-card border border-border rounded-xl p-5 mb-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-4">12-Week Cash Flow Forecast</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={CASHFLOW}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000000).toFixed(1)}M`} />
            <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
            <Line type="monotone" dataKey="cumulative" stroke="#288CC8" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <Button onClick={() => toast.success("Monthly Management Pack generated")} className="bg-[#00283C] hover:bg-[#00283C]/80 font-body text-sm">
        <FileText className="w-4 h-4 mr-2" /> Generate Monthly Management Pack
      </Button>
    </IntranetLayout>
  );
};

export default FinancialPulse;
